import React, { useState, useEffect } from 'react';
import { ChevronDown, Download, AlertCircle, Users, BookOpen, Calendar, GraduationCap, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../../auth/AuthProvider';

const ExamsMarkSheet = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter states
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  
  // Data states
  const [marksData, setMarksData] = useState(null);
  const [subjects, setSubjects] = useState([]);

  // Fetch initial data
  useEffect(() => {
    fetchAcademicYears();
    fetchClasses();
    fetchSubjects();
  }, []);

  // Fetch exams when academic year and term are selected
  useEffect(() => {
    if (selectedAcademicYear && selectedTerm) {
      fetchExams();
    }
  }, [selectedAcademicYear, selectedTerm]);

  // Auto-fetch marks when all filters are selected
  useEffect(() => {
    if (selectedExam && selectedClass) {
      fetchMarks();
    }
  }, [selectedExam, selectedClass]);

  const handleError = (err, context = '') => {
    console.error(`Error ${context}:`, err);
    const errorMessage = err.response?.data?.detail || err.message || 'An unexpected error occurred';
    setError(`${context ? context + ': ' : ''}${errorMessage}`);
  };

  const fetchAcademicYears = async () => {
    try {
      setError('');
      const response = await api.get('/api/v1/academic-years/list-with-terms');
      setAcademicYears(response.data);
    } catch (err) {
      handleError(err, 'Failed to fetch academic years');
    }
  };

  const fetchClasses = async () => {
    try {
      setError('');
      const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
      setClasses(response.data);
    } catch (err) {
      handleError(err, 'Failed to fetch classes');
    }
  };

  const fetchExams = async () => {
    try {
      setError('');
      const response = await api.get(`/api/v1/exams/school/${user.school_id}/all-exams`);
      const filteredExams = response.data.filter(exam => 
        exam.academic_year_id === parseInt(selectedAcademicYear) && 
        exam.term_id === parseInt(selectedTerm)
      );
      setExams(filteredExams);
    } catch (err) {
      handleError(err, 'Failed to fetch exams');
    }
  };

  const fetchSubjects = async () => {
    try {
      setError('');
      const response = await api.get('/api/v1/subjects');
      setSubjects(response.data);
    } catch (err) {
      handleError(err, 'Failed to fetch subjects');
    }
  };

  const fetchMarks = async () => {
    if (!selectedExam || !selectedClass) return;
    
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/v1/exam-analysis/raw-marks?exam_id=${selectedExam}&class_id=${selectedClass}`);
      setMarksData(response.data);
    } catch (err) {
      handleError(err, 'Failed to fetch marks data');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedAcademicYear = () => {
    return academicYears.find(year => year.academic_year_id === parseInt(selectedAcademicYear));
  };

  const getSelectedTerm = () => {
    const year = getSelectedAcademicYear();
    return year?.terms.find(term => term.term_id === parseInt(selectedTerm));
  };

  const getSelectedClass = () => {
    return classes.find(cls => cls.id === parseInt(selectedClass));
  };

  const getSelectedExam = () => {
    return exams.find(exam => exam.id === parseInt(selectedExam));
  };

  const exportToPDF = () => {
    if (!marksData) return;

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for more width
    const primaryColor = '#0E5252';
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const usableWidth = pageWidth - (margin * 2);

    // Calculate column widths
    const indexColWidth = 8;
    const admissionColWidth = 20;
    const nameColWidth = 35;
    const studentInfoWidth = indexColWidth + admissionColWidth + nameColWidth;
    const availableSubjectWidth = usableWidth - studentInfoWidth;

    // Estimate subject column width (minimum 15mm for readability)
    const minSubjectColWidth = 15;
    const maxSubjectsPerPage = Math.floor(availableSubjectWidth / minSubjectColWidth);
    const actualSubjectColWidth = availableSubjectWidth / Math.min(maxSubjectsPerPage, marksData.subjects.length);

    // Group subjects into pages
    const subjectGroups = [];
    for (let i = 0; i < marksData.subjects.length; i += maxSubjectsPerPage) {
      subjectGroups.push(marksData.subjects.slice(i, i + maxSubjectsPerPage));
    }

    const yearData = getSelectedAcademicYear();
    const termData = getSelectedTerm();
    const classData = getSelectedClass();
    const examData = getSelectedExam();

    // Function to add header to each page
    const addPageHeader = (doc, pageNum, totalPages) => {
      // Main title
      doc.setFontSize(16);
      doc.setTextColor(primaryColor);
      doc.text('EXAM MARKS REPORT', pageWidth / 2, 15, { align: 'center' });

      // School info
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      let yPos = 25;

      doc.text(`Academic Year: ${yearData?.year_name || 'N/A'}`, margin, yPos);
      doc.text(`Term: ${termData?.term_name || 'N/A'}`, margin, yPos + 5);
      doc.text(`Class: ${marksData.class_name}`, margin, yPos + 10);
      doc.text(`Exam: ${marksData.exam_name}`, margin, yPos + 15);
      doc.text(`Total Students: ${marksData.students.length}`, margin, yPos + 20);

      // Page info
      doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin, yPos, { align: 'right' });
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - margin, yPos + 5, { align: 'right' });

      return yPos + 30; // Return Y position where content should start
    };

    // Generate each page
    subjectGroups.forEach((subjectGroup, groupIndex) => {
      if (groupIndex > 0) {
        doc.addPage();
      }

      const startY = addPageHeader(doc, groupIndex + 1, subjectGroups.length);

      // Prepare headers for this page
      const pageHeaders = ['#', 'Admission No.', 'Student Name', ...subjectGroup.map(sub => sub.name)];

      // Prepare table data for this page
      const pageTableData = marksData.students.map((student, index) => {
        const row = [
          (index + 1).toString(),
          student.admission_number || 'N/A',
          student.name || 'N/A',
          ...subjectGroup.map(subject => {
            const subjectData = student.subjects[subject.id.toString()];
            return subjectData ? `${subjectData.raw_score}` : 'N/A';
          })
        ];
        return row;
      });

      // Calculate column widths for this page
      const columnWidths = [
        indexColWidth,
        admissionColWidth,
        nameColWidth,
        ...subjectGroup.map(() => actualSubjectColWidth)
      ];

      // Add table with fixed column widths
      doc.autoTable({
        head: [pageHeaders],
        body: pageTableData,
        startY: startY,
        theme: 'striped',
        headStyles: {
          fillColor: [14, 82, 82],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: 'ellipsis',
          cellWidth: 'wrap'
        },
        columnStyles: {
          0: {
            halign: 'center',
            cellWidth: indexColWidth,
            overflow: 'hidden'
          },
          1: {
            cellWidth: admissionColWidth,
            overflow: 'hidden',
            halign: 'center'
          },
          2: {
            cellWidth: nameColWidth,
            overflow: 'hidden'
          },
          // Subject columns
          ...Object.fromEntries(
            subjectGroup.map((_, idx) => [
              idx + 3,
              {
                cellWidth: actualSubjectColWidth,
                halign: 'center',
                overflow: 'hidden'
              }
            ])
          )
        },
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          overflow: 'hidden',
          lineWidth: 0.1,
          lineColor: [200, 200, 200]
        },
        margin: { left: margin, right: margin },
        tableWidth: usableWidth,
        showHead: 'everyPage',
        didDrawPage: function (data) {
          // Add page footer
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          const footerY = pageHeight - 10;
          doc.text(`Generated by: ${user.name || user.email}`, margin, footerY);

          if (subjectGroups.length > 1) {
            const subjectRange = `Subjects: ${subjectGroup[0].name} to ${subjectGroup[subjectGroup.length - 1].name}`;
            doc.text(subjectRange, pageWidth - margin, footerY, { align: 'right' });
          }
        }
      });

      // Add subject range info if multiple pages
      if (subjectGroups.length > 1) {
        const finalY = doc.lastAutoTable.finalY + 5;
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        const rangeText = `This page shows subjects: ${subjectGroup[0].name} to ${subjectGroup[subjectGroup.length - 1].name}`;
        doc.text(rangeText, margin, finalY);
      }
    });

    // Save the PDF
    const fileName = `${marksData.exam_name}_${marksData.class_name}_Marks.pdf`;
    doc.save(fileName);
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Exam Marks Analysis</h1>
              <p className="text-gray-600 mt-1">View and analyze student performance across different exams</p>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-r-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-red-800 font-medium text-sm">Error Occurred</h3>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex items-center gap-2 mb-6">
            <FileText className="w-5 h-5" style={{ color: '#0E5252' }} />
            <h2 className="text-lg font-semibold text-gray-900">Filter Options</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Academic Year */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Calendar className="w-4 h-4" style={{ color: '#0E5252' }} />
                Academic Year
              </label>
              <div className="relative">
                <select
                  value={selectedAcademicYear}
                  onChange={(e) => {
                    setSelectedAcademicYear(e.target.value);
                    setSelectedTerm('');
                    setSelectedExam('');
                    setMarksData(null);
                  }}
                  className="w-full h-12 px-4 pr-10 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 appearance-none hover:border-gray-300"
                  style={{ '--tw-ring-color': '#0E5252' }}
                >
                  <option value="">Select Year</option>
                  {academicYears.map(year => (
                    <option key={year.academic_year_id} value={year.academic_year_id}>
                      {year.year_name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Term */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <BookOpen className="w-4 h-4" style={{ color: '#0E5252' }} />
                Term
              </label>
              <div className="relative">
                <select
                  value={selectedTerm}
                  onChange={(e) => {
                    setSelectedTerm(e.target.value);
                    setSelectedExam('');
                    setMarksData(null);
                  }}
                  disabled={!selectedAcademicYear}
                  className="w-full h-12 px-4 pr-10 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 appearance-none hover:border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                  style={{ '--tw-ring-color': '#0E5252' }}
                >
                  <option value="">Select Term</option>
                  {getSelectedAcademicYear()?.terms.map(term => (
                    <option key={term.term_id} value={term.term_id}>
                      {term.term_name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Class */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Users className="w-4 h-4" style={{ color: '#0E5252' }} />
                Class
              </label>
              <div className="relative">
                <select
                  value={selectedClass}
                  onChange={(e) => {
                    setSelectedClass(e.target.value);
                    setMarksData(null);
                  }}
                  className="w-full h-12 px-4 pr-10 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 appearance-none hover:border-gray-300"
                  style={{ '--tw-ring-color': '#0E5252' }}
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Exam */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <GraduationCap className="w-4 h-4" style={{ color: '#0E5252' }} />
                Exam
              </label>
              <div className="relative">
                <select
                  value={selectedExam}
                  onChange={(e) => {
                    setSelectedExam(e.target.value);
                    setMarksData(null);
                  }}
                  disabled={!selectedAcademicYear || !selectedTerm}
                  className="w-full h-12 px-4 pr-10 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 appearance-none hover:border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                  style={{ '--tw-ring-color': '#0E5252' }}
                >
                  <option value="">Select Exam</option>
                  {exams.filter(exam => exam.class_ids.includes(parseInt(selectedClass))).map(exam => (
                    <option key={exam.id} value={exam.id}>
                      {exam.exam_name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-3 border-gray-200 border-t-[#0E5252] rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 text-lg">Loading exam marks...</p>
            </div>
          </div>
        )}

        {/* Results */}
        {marksData && !loading && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Exam</p>
                    <p className="text-lg font-semibold text-gray-900">{marksData.exam_name}</p>
                  </div>
                  <GraduationCap className="w-8 h-8 text-gray-400" />
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Class</p>
                    <p className="text-lg font-semibold text-gray-900">{marksData.class_name}</p>
                  </div>
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Subjects</p>
                    <p className="text-lg font-semibold text-gray-900">{marksData.subjects.length}</p>
                  </div>
                  <BookOpen className="w-8 h-8 text-gray-400" />
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Students</p>
                    <p className="text-lg font-semibold text-gray-900">{marksData.students.length}</p>
                  </div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: '#0E5252' }}>
                    {marksData.students.length}
                  </div>
                </div>
              </div>
            </div>

            {/* Export Button */}
            <div className="flex justify-end">
              <button
                onClick={exportToPDF}
                className="flex items-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5"
                style={{ backgroundColor: '#0E5252' }}
              >
                <Download className="w-4 h-4" />
                Export to PDF
              </button>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                        #
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                        Adm No
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                        Student Name
                      </th>
                      {marksData.subjects.map(subject => (
                        <th key={subject.id} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                          {subject.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {marksData.students.map((student, index) => (
                      <tr key={student.student_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          <span className="font-medium">{index + 1}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {student.admission_number}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">
                          {student.name}
                        </td>
                        {marksData.subjects.map(subject => {
                          const subjectData = student.subjects[subject.id.toString()];
                          return (
                            <td key={subject.id} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                              {subjectData ? (
                                <div>
                                  <div className="font-medium">{subjectData.raw_score}</div>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {marksData.students.length === 0 && (
                <div className="text-center py-16">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Found</h3>
                  <p className="text-gray-500">No students have marks recorded for this exam yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!marksData && !loading && selectedExam && selectedClass && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#0E52521A' }}>
              <GraduationCap className="w-8 h-8" style={{ color: '#0E5252' }} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
            <p className="text-gray-500">No marks data found for the selected filters.</p>
          </div>
        )}

        {!selectedExam && !loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#0E52521A' }}>
              <FileText className="w-8 h-8" style={{ color: '#0E5252' }} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select Filters</h3>
            <p className="text-gray-500">Choose academic year, term, class, and exam to view marks data.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamsMarkSheet;